import type { ReactNode } from 'react';
import { useEchap } from './useEchap';
import { OK } from './vocabulaire';
import './FeuilleDuBas.css';

/**
 * Une feuille qui monte du bas au téléphone, sous un voile, avec sa poignée et son titre : la
 * seule peau des feuilles du bas (chantier `nettoyage-fiche`, ticket 02). Le panneau Réglages
 * de « Maintenant » (`PanneauDesReglages`, chantier `telephone-cadre`, ticket 03), les listes
 * des trois boutons de « Je choisis » et de l'Agenda, « Plus tard » de la journée, les petites
 * feuilles de la fiche (`FicheAuPouce`) et le + (`Capture`). Elle défile quand la liste est
 * longue ; le voile la ferme sans rien changer.
 *
 * - `echap` : Échap la ferme (par défaut). Sans, celui qui l'ouvre le tient pour elle : la fiche
 *   du téléphone replie ses feuilles dans son ordre à elle.
 * - `ok` : « OK » à droite du titre ; `croix` : une croix à droite du titre, avec ce nom.
 * - `dessus` : par-dessus le reste, barre des onglets comprise, et plus haute (le +).
 * - `classe` et `attributs` : ce que chaque feuille ajoute à son panneau, pour son dessin à elle.
 * - `icone` : un dessin devant le titre (le ⚙ de la feuille qui règle un écran, chantier
 *   telephone-habit, 02).
 */
export function FeuilleDuBas({
  titre,
  onFermer,
  echap = true,
  ok = false,
  croix,
  dessus = false,
  classe,
  attributs,
  icone,
  children,
}: {
  titre: string;
  onFermer: () => void;
  echap?: boolean;
  ok?: boolean;
  croix?: string;
  dessus?: boolean;
  classe?: string;
  attributs?: Record<`data-${string}`, string>;
  icone?: ReactNode;
  children: ReactNode;
}) {
  useEchap(echap, onFermer);

  const tete = ok || croix !== undefined;
  const nom = (
    <h2 className="panneau-reglages-titre">
      {icone}
      {titre}
    </h2>
  );
  return (
    <>
      <div className={`panneau-reglages-voile${dessus ? ' panneau-reglages-voile--dessus' : ''}`} onClick={onFermer} aria-hidden="true" />
      <div
        className={['panneau-reglages', dessus && 'panneau-reglages--dessus', classe].filter(Boolean).join(' ')}
        role="dialog"
        aria-label={titre}
        {...attributs}
      >
        <div className="panneau-reglages-poignee" aria-hidden="true" />
        {tete ? (
          <div className="panneau-reglages-tete">
            {nom}
            {ok && (
              <button type="button" className="panneau-reglages-ok" onClick={onFermer}>
                {OK}
              </button>
            )}
            {croix !== undefined && (
              <button type="button" className="panneau-reglages-croix" aria-label={croix} onClick={onFermer}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2.5 2.5l9 9M11.5 2.5l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          nom
        )}
        {children}
      </div>
    </>
  );
}
